/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import _ from 'lodash';
import React from 'react';
import { decorate, computed, runInAction, observable, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import { Icon } from 'semantic-ui-react';
import { displayError } from '@amzn/base-ui/dist/helpers/notification';
import Dropdown from '@amzn/base-ui/dist/parts/helpers/fields/DropDown';
import Form from '@amzn/base-ui/dist/parts/helpers/fields/Form';
import Input from '@amzn/base-ui/dist/parts/helpers/fields/Input';
import TextArea from '@amzn/base-ui/dist/parts/helpers/fields/TextArea';

import { getCreateInternalPlatformForm } from '../../../models/forms/CreateInternalPlatformForm';
import SelectConfigurationCards from './SelectConfigurationCards';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

// expected props
// - onPrevious (via props)
// - onNext (via props) a function is called with the form data
// - platformId (via props)
// - configurations (via props)
// - title (via props)
// - defaultCidr (via props)
// - clientInformationStore (via injection)
// - userStore (via injection)
class CreateInternalPlatformForm extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.form = getCreateInternalPlatformForm({
        projectIdOptions: this.projectIdOptions,
        cidr: this.props.defaultCidr,
      });
    });
  }

  get projectIdOptions() {
    const store = this.userStore;
    return store.projectIdDropdown;
  }

  get platformId() {
    return this.props.platformId;
  }

  get configurations() {
    return this.props.configurations;
  }

  get userStore() {
    return this.props.userStore;
  }

  // eslint-disable-next-line consistent-return
  handlePrevious = () => {
    if (_.isFunction(this.props.onPrevious)) return this.props.onPrevious();
  };

  // eslint-disable-next-line consistent-return
  handleNext = async form => {
    const data = { ...form.values(), params: {}, platformId: this.platformId };

    const configuration = _.find(this.configurations, ['id', data.configurationId]);
    _.forEach(_.keys(configuration.mutableParams), key => {
      if (!_.has(data, key)) return;
      data.params[key] = data[key];
      delete data[key];
    });

    try {
      await this.props.onNext(data);
    } catch (error) {
      displayError(error);
    }
  };

  render() {
    const title = this.props.title || '';
    return (
      <div className="mt2">
        <h3 className="workspace-form-title">{title}</h3>
        {this.renderForm()}
      </div>
    );
  }

  renderForm() {
    const form = this.form;
    const askForCidr = !_.isUndefined(this.props.defaultCidr);
    const configurations = this.configurations;

    return (
      <div className="workspace-form-card">
        <Form form={form} onCancel={this.handlePrevious} onSuccess={this.handleNext}>
          {({ processing, onCancel }) => (
            <>
              <Input field={form.$('name')} />
              {askForCidr && <Input field={form.$('cidr')} />}
              <Dropdown field={form.$('projectId')} fluid selection />
              <SelectConfigurationCards configurations={configurations} formField={form.$('configurationId')} />
              <TextArea field={form.$('description')} />

              <div className="workspace-form-buttons">
                <button
                  className="workspace-action-btn workspace-action-btn-secondary"
                  disabled={processing}
                  onClick={onCancel}
                  type="button"
                >
                  <Icon name="left arrow" style={iconStyle} /> Previous
                </button>
                <button
                  className="workspace-action-btn workspace-action-btn-primary"
                  disabled={processing}
                  type="submit"
                >
                  Create Workspace
                </button>
              </div>
            </>
          )}
        </Form>
      </div>
    );
  }
}

decorate(CreateInternalPlatformForm, {
  form: observable,
  platformId: computed,
  configurations: computed,
  userStore: computed,
  projectIdOptions: computed,
  handlePrevious: action,
});

export default inject('userStore', 'clientInformationStore')(observer(CreateInternalPlatformForm));
